/**
 * Content Prompts Constants
 * 
 * Unified prompt templates, tones, CTAs, and quick actions for all platforms.
 * Used by the chat interface wizard and content generation.
 * 
 * @module contentPrompts
 */

// =============================================================================
// LINKEDIN
// =============================================================================

export const LINKEDIN_TEMPLATES = {
    insight: {
        name: 'Key Insight',
        prompt: 'Create a LinkedIn post highlighting the most valuable insight from this episode. \n\nRULES:\n- Start with a punchy, 1-line hook (no "Here is an insight...").\n- Use single-sentence paragraphs to optimize for mobile scrolling.\n- Do NOT use emojis.\n- Do NOT use concluding phrases like "In conclusion", "Let\'s dive in", or "What do you think?".\n- Place all hashtags at the very bottom, separated by a blank line.',
        description: 'Share a powerful insight that will resonate with your audience'
    },
    story: {
        name: 'Story-Driven',
        prompt: 'Write a LinkedIn post that tells a story from the episode. \n\nRULES:\n- Start by establishing the conflict or stakes in the first line.\n- Use narrative pacing (mix short and medium sentences).\n- No bullet points—tell a flowing story.\n- Do NOT use words like "Transformative", "Unveiled", or "Delve".\n- End with a single takeaway sentence.',
        description: 'Engage with a compelling narrative'
    },
    hotTake: {
        name: 'Hot Take',
        prompt: 'Create a thought-provoking LinkedIn post that challenges conventional thinking on a topic from the episode.\n\nRULES:\n- The first line MUST be a bold, slightly controversial statement.\n- Immediately back it up with reasoning from the episode.\n- Use minimal formatting (no bold/italic text).\n- Avoid formal conclusions or "AI voice". Speak like a real person.',
        description: 'Share a bold perspective'
    },
    takeaways: {
        name: 'Key Takeaways',
        prompt: 'Write a LinkedIn post with 3 key takeaways from the episode.\n\nRULES:\n- Do not use "Here are 3 takeaways". Write a compelling 1-2 sentence intro instead.\n- Keep each takeaway under 2 sentences.\n- Use simple text bullets (-, *, or numbers), no emojis.\n- Add exactly one blank line between each takeaway for readability.',
        description: 'Deliver value in a scannable format'
    },
    quote: {
        name: 'Quote Highlight',
        prompt: 'Create a LinkedIn post centered around a powerful quote from the guest.\n\nRULES:\n- Put the quote at the very top, enclosed in standard quotes.\n- Follow up with 2-3 short paragraphs explaining why it matters.\n- Keep it authentic and conversational. No jargon.',
        description: 'Highlight a memorable quote'
    },
    behindScenes: {
        name: 'Behind the Scenes',
        prompt: 'Write a LinkedIn post sharing a behind-the-scenes moment or lesson from recording this episode.\n\nRULES:\n- Write in a highly personal, first-person perspective.\n- Focus on a specific challenge, realization, or funny moment.\n- Do NOT sound promotional. It should feel like a note to a friend.',
        description: 'Share a personal moment'
    }
};

export const LINKEDIN_TONE_OPTIONS = [
    { id: 'professional', label: 'Professional', description: 'Polished and business-focused' },
    { id: 'casual', label: 'Casual', description: 'Friendly and conversational' },
    { id: 'thought-provoking', label: 'Thought-Provoking', description: 'Deep and reflective' },
    { id: 'inspirational', label: 'Inspirational', description: 'Motivating and uplifting' },
    { id: 'educational', label: 'Educational', description: 'Teaching-focused with value' }
];

export const LINKEDIN_CTA_OPTIONS = [
    { id: 'comment', label: 'Ask for Comments', prompt: 'End with a question to encourage comments.' },
    { id: 'share', label: 'Encourage Sharing', prompt: 'Ask readers to share with their network.' },
    { id: 'follow', label: 'Follow for More', prompt: 'Invite readers to follow for more content.' },
    { id: 'link', label: 'Include Link', prompt: 'Include a link to the full episode.' },
    { id: 'none', label: 'No CTA', prompt: 'No specific call-to-action.' }
];

export const LINKEDIN_QUICK_ACTIONS = [
    { id: 'make-shorter', label: 'Make Shorter', prompt: 'Make this LinkedIn post more concise while keeping the key message.' },
    { id: 'make-longer', label: 'Expand', prompt: 'Expand this LinkedIn post with more detail and context.' },
    { id: 'more-engaging', label: 'More Engaging', prompt: 'Rewrite this LinkedIn post with a stronger hook and more engaging language.' },
    { id: 'add-hashtags', label: 'Add Hashtags', prompt: 'Add 3-5 relevant hashtags to this LinkedIn post.' },
    { id: 'change-tone', label: 'Change Tone', prompt: 'Rewrite this LinkedIn post with a more professional tone.' },
    { id: 'improve-hook', label: 'Improve Hook', prompt: 'Rewrite the opening hook to be more scroll-stopping.' }
];

// =============================================================================
// TWITTER
// =============================================================================

export const TWITTER_TEMPLATES = {
    announcement: {
        name: 'Announcement',
        prompt: 'Create a tweet announcing this episode. \n\nRULES:\n- MUST be under 280 characters.\n- Start with the core value proposition (why should they listen?).\n- Do not use more than 2 emojis.\n- Avoid generic phrases like "I\'m excited to announce".',
        description: 'Announce your new episode'
    },
    quote: {
        name: 'Quote Highlight',
        prompt: 'Create a tweet centered around a powerful quote from the guest.\n\nRULES:\n- Start directly with the quote enclosed in "".\n- Add a 1-sentence thought underneath.\n- Do NOT use hashtags inline.',
        description: 'Share a memorable quote'
    },
    hotTake: {
        name: 'Hot Take',
        prompt: 'Create a bold, thought-provoking tweet about a key topic from the episode.\n\nRULES:\n- Be punchy and direct. Start with a contrarian statement.\n- MUST be under 280 characters.\n- Speak natively for Twitter (casual, assertive).',
        description: 'Share a bold perspective'
    },
    thread: {
        name: 'Thread',
        prompt: 'Create a Twitter thread breaking down key insights from the episode.\n\nRULES:\n- The first tweet MUST be an irresistible hook (state the problem + the promise).\n- End the first tweet with a thread emoji 🧵.\n- Limit to 3-5 tweets max.\n- Number each subsequent tweet (e.g. 1/, 2/).\n- Keep each tweet under 280 characters.',
        description: 'Create an engaging thread'
    },
    engagement: {
        name: 'Engagement Question',
        prompt: 'Create a question-based tweet to spark conversation about the episode topic.\n\nRULES:\n- Ask a specific, "this or that", or experience-based question.\n- Do NOT ask broad questions like "What are your thoughts?"\n- Keep it under 200 characters to leave room for quote retweets.',
        description: 'Spark a conversation'
    }
};

export const TWITTER_TONE_OPTIONS = [
    { id: 'witty', label: 'Witty', description: 'Clever and humorous' },
    { id: 'casual', label: 'Casual', description: 'Friendly and conversational' },
    { id: 'provocative', label: 'Provocative', description: 'Bold and thought-provoking' },
    { id: 'informative', label: 'Informative', description: 'Educational and valuable' },
    { id: 'inspirational', label: 'Inspirational', description: 'Motivating and uplifting' }
];

export const TWITTER_CTA_OPTIONS = [
    { id: 'retweet', label: 'Ask for Retweet', prompt: 'Ask followers to retweet.' },
    { id: 'reply', label: 'Ask for Reply', prompt: 'End with a question to encourage replies.' },
    { id: 'follow', label: 'Follow for More', prompt: 'Invite followers to follow for more.' },
    { id: 'link', label: 'Include Link', prompt: 'Include a link to the full episode.' },
    { id: 'none', label: 'No CTA', prompt: 'No specific call-to-action.' }
];

export const TWITTER_QUICK_ACTIONS = [
    { id: 'make-shorter', label: 'Make Shorter', prompt: 'Make this tweet shorter while keeping the impact.' },
    { id: 'make-punchier', label: 'Make Punchier', prompt: 'Rewrite this tweet with a stronger hook and more impact.' },
    { id: 'add-hashtags', label: 'Add Hashtags', prompt: 'Add 2-3 relevant hashtags to this tweet.' },
    { id: 'make-thread', label: 'Convert to Thread', prompt: 'Convert this into a Twitter thread with 2-3 tweets.' },
    { id: 'add-emoji', label: 'Add Emoji', prompt: 'Add relevant emojis to make this tweet more engaging.' }
];

// =============================================================================
// FACEBOOK
// =============================================================================

export const FACEBOOK_TEMPLATES = {
    highlight: {
        name: 'Episode Highlight',
        prompt: 'Create a Facebook post highlighting the key moments and takeaways from this episode.\n\nRULES:\n- Use a friendly, conversational tone.\n- Keep paragraphs to 1-2 sentences.\n- Avoid corporate jargon or overly formal conclusions.\n- End with a single clear question to drive comments.',
        description: 'Share episode highlights'
    },
    engagement: {
        name: 'Engagement Post',
        prompt: 'Create a Facebook post that asks a question to encourage comments and discussion.\n\nRULES:\n- Start immediately with the question or a relatable hook.\n- Provide a brief 2-3 sentence personal take from the episode to set context.\n- Ban phrases like "What are your thoughts?" Ask something specific.',
        description: 'Encourage engagement'
    },
    behindScenes: {
        name: 'Behind the Scenes',
        prompt: 'Share a behind-the-scenes moment or lesson from recording this episode.\n\nRULES:\n- Write in the first person.\n- Keep it authentic and slightly vulnerable.\n- Use conversational formatting (mix of short/medium sentences).\n- Do NOT sound promotional.',
        description: 'Share a personal moment'
    },
    story: {
        name: 'Story Time',
        prompt: 'Write a Facebook post that tells a story from the episode.\n\nRULES:\n- Hook the reader with the core conflict or stakes in the first line.\n- Use narrative pacing. No bullet points.\n- Eliminate AI words like "Transformative", "Journey", or "Delve".\n- End with a relatable, grounded takeaway.',
        description: 'Tell a compelling story'
    }
};

export const FACEBOOK_TONE_OPTIONS = [
    { id: 'professional', label: 'Professional', description: 'Polished and business-focused' },
    { id: 'casual', label: 'Casual', description: 'Friendly and conversational' },
    { id: 'promotional', label: 'Promotional', description: 'Marketing-focused with clear CTA' },
    { id: 'storytelling', label: 'Storytelling', description: 'Narrative and engaging' },
    { id: 'inspirational', label: 'Inspirational', description: 'Motivating and uplifting' }
];

export const FACEBOOK_CTA_OPTIONS = [
    { id: 'comment', label: 'Ask for Comments', prompt: 'End with a question to encourage comments.' },
    { id: 'share', label: 'Encourage Sharing', prompt: 'Ask readers to share with their friends.' },
    { id: 'like', label: 'Ask for Like', prompt: 'Ask readers to like if they agree.' },
    { id: 'link', label: 'Include Link', prompt: 'Include a link to the full episode.' },
    { id: 'none', label: 'No CTA', prompt: 'No specific call-to-action.' }
];

export const FACEBOOK_QUICK_ACTIONS = [
    { id: 'make-shorter', label: 'Make Shorter', prompt: 'Make this Facebook post more concise.' },
    { id: 'add-question', label: 'Add Question', prompt: 'Add an engaging question at the end to encourage comments.' },
    { id: 'make-personal', label: 'Make Personal', prompt: 'Add a personal touch or story to this Facebook post.' },
    { id: 'add-hashtags', label: 'Add Hashtags', prompt: 'Add 2-3 relevant hashtags to this Facebook post.' },
    { id: 'more-engaging', label: 'More Engaging', prompt: 'Rewrite this Facebook post to be more engaging and shareable.' }
];

// =============================================================================
// INSTAGRAM
// =============================================================================

export const INSTAGRAM_TEMPLATES = {
    quote: {
        name: 'Quote Card',
        prompt: 'Create an Instagram caption for a quote card from this episode.\n\nRULES:\n- Start the caption with the quote itself or a strong hook reacting to the quote.\n- Use moderate emojis (1-2 max per paragraph).\n- Break paragraphs cleanly with blank lines.\n- Place 5-10 hashtags at the very bottom, separated from the CTA by a blank line.',
        description: 'Perfect for quote graphics'
    },
    teaser: {
        name: 'Teaser',
        prompt: 'Create a teaser caption that builds anticipation for this episode without giving everything away.\n\nRULES:\n- Focus on the "why" instead of the "what". What secret is revealed?\n- Keep it under 150 words.\n- Do NOT use formal conclusions like "In summary".\n- Place 5-10 hashtags at the very bottom.',
        description: 'Build anticipation'
    },
    takeaways: {
        name: 'Key Takeaways',
        prompt: 'Create an Instagram caption with 3 key takeaways.\n\nRULES:\n- Use a compelling 1-line hook.\n- Use emoji bullets (e.g. 💡, 🚀, or 📌) but no other inline emojis.\n- Keep each takeaway extremely brief (1 sentence max).\n- Place hashtags at the very bottom block.',
        description: 'Deliver value quickly'
    },
    saveLater: {
        name: 'Save for Later',
        prompt: 'Create an Instagram caption that encourages saving and sharing, with valuable insights.\n\nRULES:\n- The first line MUST explicitly tell them what they are saving.\n- Provide a highly actionable, formatted list or step-by-step guide.\n- End by explicitly asking them to "Save this for later" before the hashtags.',
        description: 'Encourage saves and shares'
    }
};

export const INSTAGRAM_TONE_OPTIONS = [
    { id: 'inspiring', label: 'Inspiring', description: 'Motivating and uplifting' },
    { id: 'casual', label: 'Casual', description: 'Friendly and conversational' },
    { id: 'educational', label: 'Educational', description: 'Teaching-focused with value' },
    { id: 'playful', label: 'Playful', description: 'Fun and lighthearted' },
    { id: 'aesthetic', label: 'Aesthetic', description: 'Visual and artistic' }
];

export const INSTAGRAM_CTA_OPTIONS = [
    { id: 'save', label: 'Save for Later', prompt: 'Encourage saving this post.' },
    { id: 'share', label: 'Share to Stories', prompt: 'Ask followers to share to their stories.' },
    { id: 'comment', label: 'Comment Below', prompt: 'End with a question to encourage comments.' },
    { id: 'link', label: 'Link in Bio', prompt: 'Direct followers to link in bio.' },
    { id: 'none', label: 'No CTA', prompt: 'No specific call-to-action.' }
];

export const INSTAGRAM_QUICK_ACTIONS = [
    { id: 'add-hashtags', label: 'Add Hashtags', prompt: 'Add 5-10 relevant hashtags to this Instagram caption.' },
    { id: 'add-emoji', label: 'Add Emoji', prompt: 'Add relevant emojis to make this caption more engaging.' },
    { id: 'add-cta', label: 'Add CTA', prompt: 'Add a clear call-to-action to this Instagram caption.' },
    { id: 'make-shorter', label: 'Make Shorter', prompt: 'Make this Instagram caption more concise.' },
    { id: 'more-engaging', label: 'More Engaging', prompt: 'Rewrite this caption to be more engaging and inspiring.' }
];

// =============================================================================
// SHOW NOTES
// =============================================================================

export const SHOW_NOTES_TEMPLATES = {
    comprehensive: {
        name: 'Comprehensive',
        prompt: 'Create detailed show notes with timestamps, key topics, and resources mentioned.\n\nRULES:\n- Start immediately with a 2-3 sentence overview. Do NOT use generic intros like "Welcome to another episode".\n- Format key topics using clear markdown bullet points.\n- DO NOT summarize everything; focus on actionable insights.\n- Include a dedicated "Resources Mentioned" section at the bottom.',
        description: 'Full episode documentation'
    },
    highlights: {
        name: 'Key Highlights',
        prompt: 'Create show notes focusing on the most important highlights and takeaways.\n\nRULES:\n- Write a punchy 1-paragraph summary at the top.\n- Provide a bulleted list of the top 3-5 highlights.\n- Keep bullet points to 1-2 sentences max.\n- Avoid AI clichés like "In this episode, we unpack...". Focus on the value delivered.',
        description: 'Focus on key moments'
    },
    chaptered: {
        name: 'Chaptered',
        prompt: 'Create show notes with clear chapter markers and timestamps for easy navigation.\n\nRULES:\n- Structure the entire document around timestamps.\n- Use the format `[00:00] - Chapter Title: Brief 1-sentence description.`\n- Do not include lengthy paragraphs between chapters. Keep it scannable.',
        description: 'Easy navigation format'
    }
};

export const SHOW_NOTES_TONE_OPTIONS = [
    { id: 'professional', label: 'Professional', description: 'Clean and business-like' },
    { id: 'casual', label: 'Casual', description: 'Friendly and approachable' },
    { id: 'detailed', label: 'Detailed', description: 'Thorough and comprehensive' }
];

export const SHOW_NOTES_CTA_OPTIONS = [
    { id: 'subscribe', label: 'Subscribe Prompt', prompt: 'Include a subscribe call-to-action.' },
    { id: 'review', label: 'Leave Review', prompt: 'Ask listeners to leave a review.' },
    { id: 'share', label: 'Share Episode', prompt: 'Encourage sharing the episode.' },
    { id: 'none', label: 'No CTA', prompt: 'No specific call-to-action.' }
];

export const SHOW_NOTES_QUICK_ACTIONS = [
    { id: 'add-timestamps', label: 'Add Timestamps', prompt: 'Add more detailed timestamps to the show notes.' },
    { id: 'add-links', label: 'Add Links', prompt: 'Add relevant links and resources to the show notes.' },
    { id: 'summarize', label: 'Summarize', prompt: 'Create a brief summary of the show notes.' }
];

// =============================================================================
// BLOG POST
// =============================================================================

export const BLOG_TEMPLATES = {
    comprehensive: {
        name: 'Comprehensive Article',
        prompt: 'Create a full-length blog post expanding on the episode topics.\n\nRULES:\n- Write with a human, journalist-style voice.\n- Use descriptive H2s and H3s structure.\n- Ban filler transitions like "Moreover", "Furthermore", "In the ever-evolving landscape", or "Ultimately".\n- Do NOT include a generic concluding paragraph starting with "In conclusion".',
        description: 'In-depth article format'
    },
    listicle: {
        name: 'Listicle',
        prompt: 'Create a list-style blog post with key takeaways.\n\nRULES:\n- Start with a compelling hook paragraph (max 3 sentences).\n- Number each H2 heading clearly (e.g., 1. Strategy Name).\n- Keep paragraph length highly varied for readability.\n- Do NOT use emojis in the headings.',
        description: 'Scannable list format'
    },
    howTo: {
        name: 'How-To Guide',
        prompt: 'Create a practical how-to blog post based on insights from the episode.\n\nRULES:\n- Structure as a clear step-by-step guide.\n- State the prerequisite or problem being solved in the intro.\n- Focus on actionable verbs for each step instruction.\n- Write directly to the reader ("you").',
        description: 'Actionable guide format'
    },
    opinion: {
        name: 'Opinion Piece',
        prompt: 'Create an opinion-style blog post sharing a unique perspective from the episode.\n\nRULES:\n- Take a clear, strong stance immediately in the opening paragraph.\n- Construct a persuasive argument rather than just summarizing.\n- Eliminate corporate jargon. Use active voice.',
        description: 'Thought leadership piece'
    }
};

export const BLOG_TONE_OPTIONS = [
    { id: 'professional', label: 'Professional', description: 'Polished and authoritative' },
    { id: 'casual', label: 'Casual', description: 'Friendly and approachable' },
    { id: 'educational', label: 'Educational', description: 'Teaching-focused' },
    { id: 'conversational', label: 'Conversational', description: 'Like talking to a friend' }
];

export const BLOG_CTA_OPTIONS = [
    { id: 'subscribe', label: 'Subscribe', prompt: 'Include a newsletter subscribe CTA.' },
    { id: 'comment', label: 'Ask for Comments', prompt: 'Encourage readers to comment.' },
    { id: 'share', label: 'Share Article', prompt: 'Ask readers to share the article.' },
    { id: 'related', label: 'Related Content', prompt: 'Link to related content.' },
    { id: 'none', label: 'No CTA', prompt: 'No specific call-to-action.' }
];

export const BLOG_QUICK_ACTIONS = [
    { id: 'expand', label: 'Expand Section', prompt: 'Expand this section with more detail.' },
    { id: 'add-examples', label: 'Add Examples', prompt: 'Add concrete examples to illustrate points.' },
    { id: 'improve-seo', label: 'Improve SEO', prompt: 'Optimize this blog post for SEO.' },
    { id: 'add-headings', label: 'Add Headings', prompt: 'Add more subheadings for better structure.' }
];

// =============================================================================
// EMAIL NEWSLETTER
// =============================================================================

export const EMAIL_NEWSLETTER_TEMPLATES = {
    episode: {
        name: 'Episode Announcement',
        prompt: 'Create an email newsletter announcing this new episode.\n\nRULES:\n- Write the subject line first.\n- Keep the tone highly conversational, like an email to a friend.\n- Provide the core value proposition ("why you need to listen") in the first two paragraphs.\n- Place one clear, primary CTA to listen to the episode.',
        description: 'New episode announcement'
    },
    digest: {
        name: 'Weekly Digest',
        prompt: 'Create a weekly digest email featuring this episode.\n\nRULES:\n- Write the subject line first.\n- Use a brief, personal intro from the host.\n- Structure the content using clear headers and bullet points.\n- Ensure it is scannable with bolded key terms.',
        description: 'Weekly roundup format'
    },
    exclusive: {
        name: 'Exclusive Content',
        prompt: 'Create an email with exclusive behind-the-scenes insights.\n\nRULES:\n- Write the subject line first.\n- Lean heavily into first-person storytelling ("I").\n- Share a detail or mistake that wasn\'t highly publicized in the episode.\n- Make the reader feel like an insider.',
        description: 'Exclusive for subscribers'
    },
    recap: {
        name: 'Episode Recap',
        prompt: 'Create a detailed recap email summarizing key points from this episode.\n\nRULES:\n- Write the subject line first.\n- Highlight the 3 biggest "Aha!" moments from the episode.\n- Do NOT include generic conclusions.\n- Add a singular, strong CTA at the bottom.',
        description: 'Detailed recap format'
    }
};

export const EMAIL_TONE_OPTIONS = [
    { id: 'professional', label: 'Professional', description: 'Polished and business-like' },
    { id: 'casual', label: 'Casual', description: 'Friendly and personal' },
    { id: 'exciting', label: 'Exciting', description: 'Enthusiastic and energetic' },
    { id: 'exclusive', label: 'Exclusive', description: 'VIP and insider feel' }
];

export const EMAIL_CTA_OPTIONS = [
    { id: 'listen', label: 'Listen Now', prompt: 'Primary CTA to listen to the episode.' },
    { id: 'subscribe', label: 'Subscribe', prompt: 'Ask to subscribe if not already.' },
    { id: 'share', label: 'Share with Friends', prompt: 'Encourage forwarding to friends.' },
    { id: 'reply', label: 'Reply', prompt: 'Ask for a reply to start a conversation.' },
    { id: 'none', label: 'No CTA', prompt: 'No specific call-to-action.' }
];

export const EMAIL_QUICK_ACTIONS = [
    { id: 'shorten', label: 'Make Shorter', prompt: 'Make this email more concise.' },
    { id: 'add-ps', label: 'Add P.S.', prompt: 'Add a P.S. section with additional value.' },
    { id: 'more-personal', label: 'More Personal', prompt: 'Make the email feel more personal.' },
    { id: 'stronger-cta', label: 'Stronger CTA', prompt: 'Make the call-to-action more compelling.' }
];

// =============================================================================
// PLATFORM LENGTH OPTIONS
// =============================================================================

export const LENGTH_OPTIONS = {
    linkedin: [
        { id: 'short', label: 'Short', description: '~100-150 words' },
        { id: 'medium', label: 'Medium', description: '~200-300 words' },
        { id: 'long', label: 'Long', description: '~400+ words' }
    ],
    twitter: [
        { id: 'single', label: 'Single Tweet', description: 'Under 280 characters' },
        { id: 'thread', label: 'Thread', description: '2-5 tweets' }
    ],
    facebook: [
        { id: 'short', label: 'Short', description: '~50-100 words' },
        { id: 'medium', label: 'Medium', description: '~150-250 words' },
        { id: 'long', label: 'Long', description: '~300+ words' }
    ],
    instagram: [
        { id: 'short', label: 'Short', description: '~50 words' },
        { id: 'medium', label: 'Medium', description: '~100-150 words' },
        { id: 'long', label: 'Long', description: '~200+ words' }
    ]
};

// =============================================================================
// OPTIMAL LENGTH CONSTANTS
// =============================================================================

export const OPTIMAL_LENGTHS = {
    linkedin: { optimal: 1300, absoluteMax: 3000 },
    twitter: { optimal: 200, absoluteMax: 280 },
    facebook: { optimal: 500, absoluteMax: 63206 },
    instagram: { optimal: 500, absoluteMax: 2200 }
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get templates for a specific platform
 */
export function getTemplatesForPlatform(platform) {
    const templates = {
        linkedin: LINKEDIN_TEMPLATES,
        twitter: TWITTER_TEMPLATES,
        facebook: FACEBOOK_TEMPLATES,
        instagram: INSTAGRAM_TEMPLATES,
        'show-notes': SHOW_NOTES_TEMPLATES,
        'blog-post': BLOG_TEMPLATES,
        'email-newsletter': EMAIL_NEWSLETTER_TEMPLATES
    };
    return templates[platform] || LINKEDIN_TEMPLATES;
}

/**
 * Get tone options for a specific platform
 */
export function getToneOptionsForPlatform(platform) {
    const tones = {
        linkedin: LINKEDIN_TONE_OPTIONS,
        twitter: TWITTER_TONE_OPTIONS,
        facebook: FACEBOOK_TONE_OPTIONS,
        instagram: INSTAGRAM_TONE_OPTIONS,
        'show-notes': SHOW_NOTES_TONE_OPTIONS,
        'blog-post': BLOG_TONE_OPTIONS,
        'email-newsletter': EMAIL_TONE_OPTIONS
    };
    return tones[platform] || LINKEDIN_TONE_OPTIONS;
}

/**
 * Get CTA options for a specific platform
 */
export function getCTAOptionsForPlatform(platform) {
    const ctas = {
        linkedin: LINKEDIN_CTA_OPTIONS,
        twitter: TWITTER_CTA_OPTIONS,
        facebook: FACEBOOK_CTA_OPTIONS,
        instagram: INSTAGRAM_CTA_OPTIONS,
        'show-notes': SHOW_NOTES_CTA_OPTIONS,
        'blog-post': BLOG_CTA_OPTIONS,
        'email-newsletter': EMAIL_CTA_OPTIONS
    };
    return ctas[platform] || LINKEDIN_CTA_OPTIONS;
}

/**
 * Get quick actions for a specific platform
 */
export function getQuickActionsForPlatform(platform) {
    const actions = {
        linkedin: LINKEDIN_QUICK_ACTIONS,
        twitter: TWITTER_QUICK_ACTIONS,
        facebook: FACEBOOK_QUICK_ACTIONS,
        instagram: INSTAGRAM_QUICK_ACTIONS,
        'show-notes': SHOW_NOTES_QUICK_ACTIONS,
        'blog-post': BLOG_QUICK_ACTIONS,
        'email-newsletter': EMAIL_QUICK_ACTIONS
    };
    return actions[platform] || LINKEDIN_QUICK_ACTIONS;
}

export default {
    // LinkedIn
    LINKEDIN_TEMPLATES,
    LINKEDIN_TONE_OPTIONS,
    LINKEDIN_CTA_OPTIONS,
    LINKEDIN_QUICK_ACTIONS,

    // Twitter
    TWITTER_TEMPLATES,
    TWITTER_TONE_OPTIONS,
    TWITTER_CTA_OPTIONS,
    TWITTER_QUICK_ACTIONS,

    // Facebook
    FACEBOOK_TEMPLATES,
    FACEBOOK_TONE_OPTIONS,
    FACEBOOK_CTA_OPTIONS,
    FACEBOOK_QUICK_ACTIONS,

    // Instagram
    INSTAGRAM_TEMPLATES,
    INSTAGRAM_TONE_OPTIONS,
    INSTAGRAM_CTA_OPTIONS,
    INSTAGRAM_QUICK_ACTIONS,

    // Show Notes
    SHOW_NOTES_TEMPLATES,
    SHOW_NOTES_TONE_OPTIONS,
    SHOW_NOTES_CTA_OPTIONS,
    SHOW_NOTES_QUICK_ACTIONS,

    // Blog Post
    BLOG_TEMPLATES,
    BLOG_TONE_OPTIONS,
    BLOG_CTA_OPTIONS,
    BLOG_QUICK_ACTIONS,

    // Email Newsletter
    EMAIL_NEWSLETTER_TEMPLATES,
    EMAIL_TONE_OPTIONS,
    EMAIL_CTA_OPTIONS,
    EMAIL_QUICK_ACTIONS,

    // Length options
    LENGTH_OPTIONS,
    OPTIMAL_LENGTHS,

    // Helpers
    getTemplatesForPlatform,
    getToneOptionsForPlatform,
    getCTAOptionsForPlatform,
    getQuickActionsForPlatform
};
